import { get } from 'es-toolkit/compat';
import type { Key } from 'antd/es/table/interface';

import type { JsonBalanceAsset, JsonBalanceExtended } from 'src/store/shared/balances/types';
import type { NumberFormatterOptions } from '@skynet/sky-number';
import { formatNumber } from '@skynet/sky-number';

import type { GetTotalPerAssetParams, GetTotalPerAssetSource, TotalOptions } from '../core/types';
import { getPrice } from 'src/store/utils/balances/core';
import type {
  JsonBalanceHistoryAsset,
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryItem,
} from 'src/store/shared/balance-history/types';
import { AssetType } from '@skynet/types-asset-type';

const defaultFormat = { style: 'currency' as const, format: 'precision' as const };

export function getTotal(
  assets: JsonBalanceAsset[],
  key: 'volume' | 'volumeUSD',
  opts: TotalOptions,
) {
  const hintParts: string[] = [];

  const value = assets.reduce((sum, asset) => {
    if (!opts.isTotalDoubling && asset.isDoubling) {
      return sum;
    }

    if (!opts.isSmallBalancesVisible && asset.isSmallBalance) {
      return sum;
    }

    const formatOpts: NumberFormatterOptions = {
      ...defaultFormat,
      style: 'currency',
      currency: key === 'volumeUSD' ? 'USD' : asset.token,
    };

    if (asset.assetType === AssetType.Position) {
      // if ([0, 'quote'].includes(asset.type)) {
      //   const value = Math.abs(asset[key]);
      //   hintParts.push(formatNumber(value, formatOpts));
      //   return sum + value;
      // }

      return sum;
    }

    const value = asset[key];
    hintParts.push(formatNumber(value, formatOpts));
    return sum + value;
  }, 0);

  return {
    value,
    tooltip: hintParts.join(' + '),
  };
}

export const getTotalPerAsset = <T extends GetTotalPerAssetSource>({
  source,
  token,
  key,
  selection = [],
  isSmallBalancesVisible = false,
  excludeFutures = false,
}: GetTotalPerAssetParams<T>) =>
  source.reduce((sum, item) => {
    if (selection?.length && !selection.includes(item.key)) {
      return sum;
    }

    if (!token) {
      return sum;
    }

    // When excludeFutures is set, skip derivatives/futures positions so the
    // per-token vertical sum matches how futures are excluded from the
    // horizontal per-account Total. A token present both as spot and as a
    // position still resolves to its spot asset here.
    const foundAsset = item.assets.find(
      (a) => a.token === token && (!excludeFutures || a.assetType !== AssetType.Position),
    );

    if (item?.isTotalDoubling || foundAsset?.isDoubling) {
      return sum;
    }

    if (!isSmallBalancesVisible && foundAsset?.isSmallBalance) {
      return sum;
    }

    const rawValue = Number(get(foundAsset ?? item, key, 0));

    return sum + rawValue;
  }, 0);

export const getTotalValue = (
  items: (JsonBalanceExtended | JsonBalanceHistoryExtended)[],
  selection: Key[] = [],
) =>
  items.reduce((sum, item) => {
    if (selection.length && !selection.includes(item.key)) {
      return sum;
    }

    if (item.isTotalDoubling) {
      return sum;
    }

    return sum + (item.totalVolumeUSD ?? 0);
  }, 0);

export const getTotalTooltip = (items: JsonBalanceExtended[], selection: Key[] = []) => {
  const totalTooltipValue = items.reduce<string[]>((sum, item) => {
    if (selection.length && !selection.includes(item.key)) {
      return sum;
    }

    if (!item.totalVolumeUSD) {
      return sum;
    }

    if (item?.isTotalDoubling) {
      return sum;
    }

    const value = formatNumber(item.totalVolumeUSD, { ...defaultFormat, currency: 'USD' });

    sum.push(value);

    return sum;
  }, []);

  if (totalTooltipValue.length === 1) {
    return '';
  }
  return totalTooltipValue.join(' + ');
};

const getSummaryDiffValue = (
  assets: Pick<JsonBalanceHistoryAsset, 'type' | 'comparison' | 'token'>[],
  key: 'volume' | 'volumeUSD',
  type: 'base' | 'quote',
) =>
  assets
    .filter((asset) => asset.type === type)
    .reduce((sum, asset) => {
      return sum + (asset?.comparison?.diff?.[key] ?? 0);
    }, 0);

/**
 * Computes the average execution price per unit of base asset, in USD, over
 * the compared period.
 *
 * Formula:
 *   avgPrice = Σ(quote diff in USD) / Σ(base diff in base-token units)
 *
 * Example: if an account bought 1.5 BTC against 100,000 USDT during the
 * period, the average price is 100,000 / 1.5 ≈ 66,666.67 USD per BTC.
 *
 * Numerator (quote side, in USD):
 *   - For USDT-denominated quotes the value is read via `key` (volume ≈
 *     volumeUSD for USDT, so both are USD-equivalent).
 *   - For any other quote token it falls back to `volumeUSD` so the sum is
 *     always denominated in USD regardless of the quote token.
 *
 * Denominator (base side, in base-token quantity):
 *   - Always read from `volume` — we need base-token quantity (e.g. BTC),
 *     not its USD value, otherwise the division would collapse to a
 *     unit-less number. This is intentionally independent of the `key`
 *     argument, which only controls how the numerator is read.
 *
 * Edge cases:
 *   - When there is no base movement (denominator = 0) the function returns
 *     `{ value: Infinity, tooltip: 'N/A' }` as a sentinel. Callers should
 *     guard with `isFinite` (see `AvgPrice.tsx`) and render an empty state.
 *   - When multiple base assets of different tokens are present, the token
 *     symbol used in the tooltip is taken from the first base asset found.
 *
 * The returned `tooltip` mirrors the calculation as
 * `"<quoteDiffUsd> / <baseDiff> = <avg>"` to make the source of the value
 * explicit in the UI.
 */
export const getAvgPerAccount = (
  assets: Pick<JsonBalanceHistoryAsset, 'type' | 'comparison' | 'token'>[],
) => {
  const foundBaseAsset = assets.find((asset) => asset.type === 'base');

  const quoteDiffValue = getSummaryDiffValue(assets, 'volumeUSD', 'quote');
  // Base denominator is always in base-token units, never in USD, so that
  // the division yields a price (USD per base unit). See function JSDoc.
  const baseDiffValue = getSummaryDiffValue(assets, 'volume', 'base');

  if (!baseDiffValue) {
    return { value: Infinity, tooltip: 'N/A' };
  }

  const avgValue = quoteDiffValue / baseDiffValue;

  const formatUsd: NumberFormatterOptions = { ...defaultFormat, currency: 'USD' };
  const formatToken: NumberFormatterOptions = {
    ...defaultFormat,
    currency: foundBaseAsset?.token,
    currencyPosition: 'none' as const,
  };
  const strQuoteDiff = formatNumber(quoteDiffValue, formatUsd);
  const strBaseDiff = formatNumber(baseDiffValue, formatToken);
  const strAvg = formatNumber(avgValue, formatUsd);

  return {
    value: avgValue,
    tooltip: `${strQuoteDiff} / ${strBaseDiff} = ${strAvg}`,
  };
};

const getSummaryTokenValue = (assets: JsonBalanceHistoryAsset[], type: 'quote' | 'base') => {
  const formatUsd: NumberFormatterOptions = {
    style: 'currency',
    format: 'precision',
    currency: 'USD',
  };
  const formatToken: NumberFormatterOptions = {
    style: 'currency',
    format: 'precision',
    // currencyPosition: 'none' as const,
  };
  return assets
    .filter((a) => a.type === type)
    .reduce(
      (acc, val) => {
        const lastPrice = Number(val.lastPrice ?? val.comparison?.endData?.price ?? 0);
        const price = Number(lastPrice ?? 0);
        const diffVolume = Number(val.comparison?.diff?.volume ?? 0);

        let result = price * diffVolume;

        let strResult = `${formatNumber(price, formatUsd)} * ${formatNumber(diffVolume, {
          ...formatToken,
          currency: val.token,
        })}`;

        if (!price || price === 1) {
          strResult = formatNumber(val.comparison?.diff?.volumeUSD ?? 0, formatUsd);
          result = val.comparison?.diff?.volumeUSD ?? 0;
        }

        if (!result) {
          return acc;
        }

        return {
          value: acc.value + result,
          tooltip: acc.tooltip ? `${acc.tooltip} + ${strResult}` : strResult,
        };
      },
      {
        value: 0,
        tooltip: '',
      },
    );
};

export const getPnlPerAccount = (assets: JsonBalanceHistoryAsset[]) => {
  const quoteDiff = getSummaryTokenValue(assets, 'quote');
  const baseDiff = getSummaryTokenValue(assets, 'base');
  return [quoteDiff, baseDiff].reduce(
    (acc, val) => {
      if (!val.value) {
        return acc;
      }

      return {
        value: acc.value + val.value,
        tooltip: acc.tooltip ? `${acc.tooltip} + ${val.tooltip}` : val.tooltip,
      };
    },
    { value: 0, tooltip: '' },
  );
};

export const getTotalChart = (assets: JsonBalanceHistoryItem[] = []) => {
  return assets.reduce((sum, { volume, quotePrice, lastPrice, type, token, assetType }) => {
    let price = getPrice({
      price: lastPrice,
      quotePrice,
      type,
      token,
    });

    if (assetType === AssetType.Position) {
      // if ([0, 'quote'].includes(type)) {
      //   return sum + Math.abs(volume) * price;
      // }

      return sum;
    }

    return sum + volume * price;
  }, 0);
};
